import { useState, useEffect, useCallback } from "react";

export type UserLocation = {
  latitude: number;
  longitude: number;
  /** Direction of travel in degrees clockwise from true north, or null when
   *  the fix carries none — a stationary device reports NaN, and desktop
   *  browsers never report one. Null means "no cone", never "north". */
  heading: number | null;
} | null;

// The Geolocation API reports heading as a number, NaN while the device is
// not moving, or null when the platform has no source at all; the marker
// only distinguishes "usable" from "absent".
function toFix(coords: GeolocationCoordinates): NonNullable<UserLocation> {
  return {
    latitude: coords.latitude,
    longitude: coords.longitude,
    heading:
      typeof coords.heading === "number" && Number.isFinite(coords.heading)
        ? coords.heading
        : null,
  };
}

// Why the browser has no fix for us. Each kind has a different remedy, and the
// copy names it: a denied site permission is fixed in the address bar, an
// unavailable position in the OS location toggle, and an unsupported browser
// not at all.
export type UserLocationErrorKind =
  | "denied"
  | "unavailable"
  | "unsupported";

export type UserLocationError = {
  kind: UserLocationErrorKind;
  /** One line naming the state — the locate button's tooltip. */
  title: string;
  /** The remedy — the toast body when the user presses locate anyway. */
  description: string;
};

const LOCATION_ERRORS: Record<UserLocationErrorKind, UserLocationError> = {
  denied: {
    kind: "denied",
    title: "Location blocked",
    description:
      "Allow location for this site from your browser's address bar, then reload the page.",
  },
  unavailable: {
    kind: "unavailable",
    title: "Location unavailable",
    description:
      "Check that Location Services is turned on for your browser in your system settings, then try again.",
  },
  unsupported: {
    kind: "unsupported",
    title: "Location not supported",
    description: "This browser doesn't support location.",
  },
};

export type RequestLocationResult = {
  location: UserLocation;
  error: UserLocationError | null;
};

export type UseUserLocationResult = {
  location: UserLocation;
  permissionGranted: boolean;
  error: UserLocationError | null;
  /** Re-request location permission and start watching if granted. Resolves
   *  with the outcome so a press handler can act on it without waiting for a
   *  re-render: center on the fix, or surface the error. */
  requestPermission: () => Promise<RequestLocationResult>;
};

function classifyError(err: GeolocationPositionError): UserLocationError {
  return err.code === err.PERMISSION_DENIED
    ? LOCATION_ERRORS.denied
    : LOCATION_ERRORS.unavailable;
}

// Browser geolocation has no separate permission-request step: the first
// getCurrentPosition/watchPosition call triggers the native prompt. Once a
// site is denied the browser remembers it and every later call fails at once
// without re-prompting, so the error is kept in state for the UI to explain
// the remedy. Two-phase shape (permission gate → watch): requestPermission
// probes with a one-shot getCurrentPosition, and only once granted does the
// watch subscription spin up.
export function useUserLocation(accuracySensors = false): UseUserLocationResult {
  const [location, setLocation] = useState<UserLocation>(null);
  const [permissionGranted, setPermissionGranted] = useState(false);
  const [error, setError] = useState<UserLocationError | null>(null);

  const requestPermission = useCallback(async () => {
    // An insecure origin (a LAN IP over http://) fails with the same
    // PERMISSION_DENIED code as a blocked site; it is a dev-only setup, so it
    // stays quiet rather than raising the "blocked" remedy.
    if (typeof window !== "undefined" && window.isSecureContext === false) {
      setError(null);
      setPermissionGranted(false);
      return { location: null, error: null };
    }
    const upfront =
      typeof navigator === "undefined" || !navigator.geolocation
        ? LOCATION_ERRORS.unsupported
        : null;
    if (upfront) {
      setError(upfront);
      setPermissionGranted(false);
      return { location: null, error: upfront };
    }
    return new Promise<RequestLocationResult>((resolve) => {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const fix = toFix(pos.coords);
          setError(null);
          setPermissionGranted(true);
          setLocation(fix);
          resolve({ location: fix, error: null });
        },
        (err) => {
          const classified = classifyError(err);
          setError(classified);
          setPermissionGranted(false);
          resolve({ location: null, error: classified });
        },
        { enableHighAccuracy: accuracySensors },
      );
    });
  }, [accuracySensors]);

  useEffect(() => {
    void requestPermission();
  }, [requestPermission]);

  useEffect(() => {
    if (!permissionGranted) return;
    if (typeof navigator === "undefined" || !navigator.geolocation) return;

    let cancelled = false;

    const watchId = navigator.geolocation.watchPosition(
      (pos) => {
        if (!cancelled) {
          setLocation(toFix(pos.coords));
        }
      },
      () => {
        // Transient errors are ignored — a later fix delivers an update.
      },
      { enableHighAccuracy: accuracySensors, maximumAge: 5000 },
    );

    return () => {
      cancelled = true;
      navigator.geolocation.clearWatch(watchId);
    };
  }, [permissionGranted, accuracySensors]);

  return { location, permissionGranted, error, requestPermission };
}
